import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import type { Team } from './typen'

const FARBEN = ['#15406d', '#3fd3b3', '#f5a623', '#26a98a', '#2a6497', '#d8453f', '#6b7c8f']

interface Formular {
  id: number | null
  kuerzel: string
  name: string
  beschreibung: string
  farbe: string
  betriebshofId: number | null
  kursnummern: string[]
  aktiv: boolean
}

export function Teams() {
  const { hatRolle } = useAuth()
  const darfPflegen = hatRolle('ADMIN', 'PERSONAL', 'DISPONENT')

  const [teams, setTeams] = useState<Team[]>([])
  const [kursnummern, setKursnummern] = useState<string[]>([])
  const [betriebshoefe, setBetriebshoefe] = useState<{ id: number; name: string }[]>([])
  const [laedt, setLaedt] = useState(true)
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [formular, setFormular] = useState<Formular | null>(null)

  async function laden() {
    setLaedt(true)
    try {
      const [t, k, b] = await Promise.all([
        api.get<Team[]>('/api/teams'),
        api.get<string[]>('/api/fahrpersonal/kursnummern'),
        api.get<{ id: number; name: string }[]>('/api/stammdaten/betriebshoefe'),
      ])
      setTeams(t)
      setKursnummern(k)
      setBetriebshoefe(b)
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    } finally {
      setLaedt(false)
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  function neu() {
    setFormular({
      id: null,
      kuerzel: '',
      name: '',
      beschreibung: '',
      farbe: FARBEN[teams.length % FARBEN.length],
      betriebshofId: betriebshoefe[0]?.id ?? null,
      kursnummern: [],
      aktiv: true,
    })
  }

  function bearbeite(t: Team) {
    setFormular({
      id: t.id,
      kuerzel: t.kuerzel,
      name: t.name,
      beschreibung: t.beschreibung ?? '',
      farbe: t.farbe ?? FARBEN[0],
      betriebshofId: t.betriebshofId,
      kursnummern: [...t.kursnummern],
      aktiv: t.aktiv,
    })
  }

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!formular) return
    setMeldung(null)
    try {
      const koerper = {
        kuerzel: formular.kuerzel,
        name: formular.name,
        beschreibung: formular.beschreibung || null,
        farbe: formular.farbe,
        betriebshofId: formular.betriebshofId,
        kursnummern: formular.kursnummern,
        aktiv: formular.aktiv,
      }
      if (formular.id === null) {
        await api.post('/api/teams', koerper)
      } else {
        await api.put(`/api/teams/${formular.id}`, koerper)
      }
      setFormular(null)
      await laden()
      setMeldung({ art: 'erfolg', text: 'Team gespeichert' })
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    }
  }

  function schalteKurs(nummer: string) {
    if (!formular) return
    const drin = formular.kursnummern.includes(nummer)
    setFormular({
      ...formular,
      kursnummern: drin
        ? formular.kursnummern.filter((k) => k !== nummer)
        : [...formular.kursnummern, nummer],
    })
  }

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Teams</h1>
          <p>
            Teams bündeln Fahrer mit gleicher Kurserfahrung. Wer in einem Team ist, darf dessen
            Kurse fahren — Abweichungen einzelner Fahrer stehen beim Mitarbeiter.
          </p>
        </div>
        {!formular && darfPflegen && (
          <button className="knopf-akzent" onClick={neu}>
            Team anlegen
          </button>
        )}
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {formular && (
        <div className="karte abschnitt">
          <h2>{formular.id === null ? 'Neues Team' : `Team ${formular.name}`}</h2>
          <form onSubmit={speichern}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 14,
              }}
            >
              <div className="feld">
                <label>Kürzel *</label>
                <input
                  type="text"
                  required
                  value={formular.kuerzel}
                  onChange={(e) => setFormular({ ...formular, kuerzel: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Name *</label>
                <input
                  type="text"
                  required
                  value={formular.name}
                  onChange={(e) => setFormular({ ...formular, name: e.target.value })}
                />
              </div>
              <div className="feld">
                <label>Betriebshof</label>
                <select
                  value={formular.betriebshofId ?? ''}
                  onChange={(e) =>
                    setFormular({
                      ...formular,
                      betriebshofId: e.target.value ? Number(e.target.value) : null,
                    })
                  }
                >
                  <option value="">—</option>
                  {betriebshoefe.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="feld">
                <label>Farbe</label>
                <div style={{ display: 'flex', gap: 6, paddingTop: 4 }}>
                  {FARBEN.map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFormular({ ...formular, farbe: f })}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: f,
                        border:
                          formular.farbe === f
                            ? '3px solid var(--navy-900)'
                            : '1px solid var(--grau-300)',
                        padding: 0,
                      }}
                      aria-label={`Farbe ${f}`}
                    />
                  ))}
                </div>
              </div>
              <div className="feld" style={{ gridColumn: '1 / -1' }}>
                <label>Beschreibung</label>
                <input
                  type="text"
                  value={formular.beschreibung}
                  onChange={(e) => setFormular({ ...formular, beschreibung: e.target.value })}
                />
              </div>
            </div>

            <div className="feld">
              <label>Kurserfahrung ({formular.kursnummern.length} von {kursnummern.length})</label>
              <div
                className="karte"
                style={{
                  padding: 12,
                  boxShadow: 'none',
                  background: 'var(--grau-50)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                }}
              >
                {kursnummern.length === 0 ? (
                  <span style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
                    Noch keine Kurse im Bestand — bitte zuerst Kursblätter einlesen.
                  </span>
                ) : (
                  kursnummern.map((k) => {
                    const drin = formular.kursnummern.includes(k)
                    return (
                      <button
                        key={k}
                        type="button"
                        className={drin ? 'knopf-akzent' : 'knopf-schlicht'}
                        style={{ padding: '5px 12px', fontSize: '0.84rem' }}
                        onClick={() => schalteKurs(k)}
                      >
                        {k}
                      </button>
                    )
                  })
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" className="knopf-primaer">
                Speichern
              </button>
              <button type="button" className="knopf-schlicht" onClick={() => setFormular(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="tabelle-huelle">
        {laedt ? (
          <div className="laden">Wird geladen …</div>
        ) : teams.length === 0 ? (
          <div className="leer">
            Noch keine Teams angelegt. Teams bestimmen, welche Kurse ein Fahrer übernehmen kann.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 40 }} />
                <th>Kürzel</th>
                <th>Name</th>
                <th>Betriebshof</th>
                <th>Fahrer</th>
                <th>Kurse</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        background: t.farbe ?? 'var(--grau-300)',
                      }}
                    />
                  </td>
                  <td>
                    <strong>{t.kuerzel}</strong>
                  </td>
                  <td>{t.name}</td>
                  <td>{t.betriebshofName ?? '—'}</td>
                  <td className="zahl">{t.anzahlFahrer}</td>
                  <td>
                    {t.kursnummern.length === 0 ? (
                      <span style={{ color: 'var(--grau-500)' }}>keine</span>
                    ) : (
                      <>
                        <strong>{t.kursnummern.length}</strong>{' '}
                        <span style={{ color: 'var(--grau-500)', fontSize: '0.82rem' }}>
                          {t.kursnummern.slice(0, 6).join(', ')}
                          {t.kursnummern.length > 6 && ' …'}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`etikett ${t.aktiv ? 'gruen' : 'grau'}`}>
                      {t.aktiv ? 'aktiv' : 'stillgelegt'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {darfPflegen && (
                      <button
                        className="knopf-schlicht"
                        style={{ padding: '5px 12px' }}
                        onClick={() => bearbeite(t)}
                      >
                        Bearbeiten
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
